import { useCallback } from 'react';

import { IntentCreationCallbackParams } from '@stripe/stripe-react-native';

/**
 * This is the function called by the Stripe SDK to confirm the payment.
 */
export const useStripeConfirmHandler = (params: UseStripeConfirmHandlerParams) => {
  const { createPaymentIntent, onPaymentIntentCreated } = params;

  const stripeConfirmHandler = useCallback(
    async (result: PaymentResult, shouldSetupFutureUsage: boolean, callback: Callback) => {
      const { id, paymentMethodType } = result;

      const paymentIntent = await createPaymentIntent(
        id,
        paymentMethodType,
        shouldSetupFutureUsage,
      ).catch(() => undefined);

      if (!paymentIntent) {
        callback(STRIPE_FAILURE);
        return;
      }

      onPaymentIntentCreated?.(paymentIntent);
      callback({ clientSecret: paymentIntent.clientSecret });
    },
    [createPaymentIntent, onPaymentIntentCreated],
  );

  return { stripeConfirmHandler };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type PaymentResult = {
  id: string;
  paymentMethodType: string;
  shouldSetupFutureUsage?: boolean;
};

type PaymentIntent = {
  orderId: string;
  paymentIntentId: string;
  clientSecret: string;
};

type Callback = (result: IntentCreationCallbackParams) => void;

type ConfirmPaymentIntent = (
  paymentMethodId: string,
  paymentMethodType: string,
  shouldSetupFutureUsage?: boolean,
) => Promise<PaymentIntent | undefined>;

type UseStripeConfirmHandlerParams = {
  createPaymentIntent: ConfirmPaymentIntent;
  onPaymentIntentCreated?: (paymentIntent: PaymentIntent) => void;
};

// ─── Constants ───────────────────────────────────────────────────────────────

const STRIPE_FAILURE = {
  error: { code: 'Failed' as const, message: 'Stripe failed to confirm the payment' },
};
